import { __ } from '@wordpress/i18n';
import PanelSettings from './advance/inspector-control';
import Advance from './advance';
import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import { VideoHtml, MouseFollower } from '@vayu-blocks';
import { VayuBlocksUniqueId } from '@vayu-blocks/global';
import Lottie from 'lottie-react';
import { useEffect, useRef, useState } from '@wordpress/element';
import LottieUploader from './lottie-uploader';
import { VayuBlocksCssJustify, boxShadow, VayuBlocksCssborderFrame } from "@vayu-blocks/global";
import { LottieToolbar } from './advance/block-control';

const edit = (props) => {
	const { attributes, setAttributes, clientId, isSelected } = props;
	const view = VayuBlocksDevice();
    const lottieRef = useRef(null);
	const wrapperRef = useRef(null);

	VayuBlocksUniqueId({ setAttributes, clientId });

	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		if (!isReady || !lottieRef.current) return;

		const anim = lottieRef.current;

		anim.setSpeed(attributes.speed || 1);
		anim.setDirection(attributes.direction ? -1 : 1);
	}, [isReady, attributes.speed, attributes.direction]);

	useEffect(() => {
		if (!isReady || !lottieRef.current) return;

		const anim = lottieRef.current;

		if (attributes.start !== undefined || attributes.end !== undefined) {
			const start = attributes.start !== undefined ? attributes.start : 0;
			const end = attributes.end !== undefined ? attributes.end : attributes.totaltime;
			anim.playSegments([start, end], true);
		} else {
			anim.playSegments([0, attributes.totaltime], true);
		}

		// ✅ Re-run animation when trigger is 'none'
		if (attributes.trigger?.type === 'none') {
			// Rewind to start and play full
			anim.stop();
			anim.playSegments([0, attributes.totaltime], true);
		}
	}, [isReady, attributes.start, attributes.end, attributes.totaltime, attributes.trigger]);

	const startFrame = attributes.start ?? 0;
	const endFrame = attributes.end ?? attributes.totaltime ?? 100;
	const midFrame = Math.floor((endFrame - startFrame) / 2 + startFrame);

	const interactivity =
		attributes.trigger?.type === 'viewport' && attributes.lottieData
			? {
					mode: 'scroll',
					actions: [
						{
							visibility: [
								(attributes.trigger?.bottom ?? 0) / 100,
								(attributes.trigger?.top ?? 100) / 100,
							],
							type: 'seek',
							frames: [startFrame, endFrame],
						},
					],
			}
		: attributes.trigger?.type === 'scroll' && attributes.lottieData
			? {
					mode: 'scroll',
					actions: [
						{
							type: 'stop',
							visibility: [
								(attributes.trigger?.bottom ?? 0) / 100,
								(attributes.trigger?.bottom ?? 0) / 100 + 0.05,
							],
							frames: [startFrame],
						},
						{
							type: 'seek',
							visibility: [
								(attributes.trigger?.bottom ?? 0) / 100 + 0.05,
								(attributes.trigger?.top ?? 100) / 100 - 0.05,
							],
							frames: [startFrame, midFrame],
						},
						{
							type: attributes.loop ? 'loop' : 'play',
							visibility: [
								(attributes.trigger?.top ?? 100) / 100 - 0.05,
								(attributes.trigger?.top ?? 100) / 100,
							],
							frames: [midFrame, endFrame],
						},
					],
			}
		: undefined;

	const [isPlaying, setIsPlaying] = useState(true);
	const [hover, sethover] = useState(false);

	const handleClick = () => {
		if (attributes.trigger?.type !== 'click' || !lottieRef.current) return;

		if (isPlaying) {
			lottieRef.current.pause();
			setIsPlaying(false);
		} else {
			if (attributes.start !== undefined || attributes.end !== undefined) {
				const start = attributes.start ?? 0;
				const end = attributes.end ?? attributes.totaltime ?? 100;
				lottieRef.current.playSegments([start, end], true);
			} else {
				lottieRef.current.play();
			}
			setIsPlaying(true);
		}
	};

	const handleMouseEnter = () => {
		if (attributes.trigger?.type !== 'hover' || !lottieRef.current || !isReady) return;

		if (attributes.start !== undefined || attributes.end !== undefined) {
			const start = attributes.start ?? 0;
			const end = attributes.end ?? attributes.totaltime ?? 100;
			lottieRef.current.playSegments([start, end], true);
		} else {
			lottieRef.current.play();
		}
	};

	const handleMouseLeave = () => {
		if (attributes.trigger?.type !== 'hover' || !lottieRef.current || !isReady) return;

		lottieRef.current.pause(); // You can use stop() if you want to reset
	};

	const getFilterValue = (key, unit = '', def = 0) => {
		const hoverVal = attributes.hoverfilter?.[key];
		const normalVal = attributes.filter?.[key];

		const value = hover
			? (hoverVal !== undefined ? hoverVal : normalVal ?? def)
			: (normalVal ?? def);

		return `${value}${unit}`;
	};

	const activeOpacity = hover
		? (attributes.opacityhvr !== undefined ? attributes.opacityhvr : attributes.opacity ?? 1)
		: (attributes.opacity ?? 1);

	const lottiestyle = {
		width: attributes.width?.[view] || '100%',
		height: 'auto',
		filter: `
			blur(${getFilterValue('blur', 'px')})
			brightness(${getFilterValue('brightness', '%', 100)})
			contrast(${getFilterValue('contrast', '%', 100)})
			saturate(${getFilterValue('saturation', '%', 100)})
			hue-rotate(${getFilterValue('hue', 'deg')})
		`.trim().replace(/\s+/g, ' '),
		opacity: activeOpacity,
		transition: `all ${attributes.transition}s ease`,
		...boxShadow(attributes.dropshadow, view),
		...VayuBlocksCssborderFrame(attributes.frameData, view),
		boxSizing:'border-box',
		background: hover ? (attributes.backgroundhvr || attributes.background) : attributes.background,
		display:'flex',
		alignItems:'center',
		justifyContent:'center'
	};

	const lottiecontainer = {
		...VayuBlocksCssJustify(attributes.alignment, view),
		display:'flex',
		alignItems:'center',
		transform:`rotate(${attributes.rotation}deg)`,
	}

	return (
		<>

			{isSelected && (
                <LottieToolbar
                    attributes={attributes}
                    setAttributes={setAttributes}
                />
            )}

			<PanelSettings attributes={attributes} setAttributes={setAttributes} isSelected={isSelected} clientId={clientId} />
			<Advance attributes={attributes} clientId={clientId}>
				<VideoHtml bg={attributes.advBackground} />

				<div className="vb-lottie-wrapper" ref={wrapperRef} style={lottiecontainer}>
					{!attributes.lottieData ? (
						<LottieUploader attributes={attributes} setAttributes={setAttributes} />
					) : (
						
						<Lottie
							lottieRef={lottieRef}
							onDOMLoaded={() => setIsReady(true)}
							animationData= {attributes.lottieData}
							loop={true}
							autoplay={attributes.trigger?.type === 'click' ? false : true}
							style={lottiestyle}
							interactivity={interactivity}
							onClick={attributes.trigger?.type === 'click' ? handleClick : undefined}
							onMouseEnter={(e) => {
								sethover(true);
								if (attributes.trigger?.type === 'hover') {
									handleMouseEnter();
								}
							}}
							onMouseLeave={(e) => {
								sethover(false);
								if (attributes.trigger?.type === 'hover') {
									handleMouseLeave();
								}
							}}
						/>
				
					)}
				</div>

				<MouseFollower
					value={attributes.advFollower}
					clientId={clientId}
				/>

			</Advance>
		</>
	);
};

export default edit;